import { Link, useParams } from "react-router-dom";
import { ArrowRight, ChevronRight, Info } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import NotFound from "@/pages/NotFound";
import { GuideBlocks, GuideImg, guideLink } from "@/components/guides/GuideBody";
import { GUIDE_IMAGES } from "@/content/guides/images";
import { renderInline } from "@/lib/miniMarkdown";
import { SsrNavigate, useHostname } from "@/lib/ssr";
import { isGuidesEnabled } from "@/lib/guidesFlag";
import { isNewHomeEnabled } from "@/lib/homeFlag";
import { blogHref, mapBlockLinks, mapInlineLinks } from "@/lib/blogLinks";
import { findAuthor } from "@/content/guides/authors";
import { soraOnly, useSoraHeadings } from "@/lib/siteFont";
import { BLOG_INDEX_SEO, BLOG_PATH, BLOG_POSTS, BLOG_PRODUCTS, blogPath, getPost, isNewArticle, type BlogPost } from "@/content/blog";
import { GUIDES_PATH, formatGuideDate, getGuide, guidePath, readingMinutes } from "@/content/guides";

// The restored Photohawk blog: /blog and /blog/<slug>, server-rendered from src/content/blog/*.md. Live everywhere
// (not behind the guides flag). A post that a pillar guide now covers is a 301 to it wherever the guides are on.

/** Where a post's URL goes: the guide that replaced it (guides on), else the post itself. */
export const postHref = (p: BlogPost, guidesOn: boolean) => (guidesOn && p.supersededBy ? guidePath(p.supersededBy) : blogPath(p.slug));

/** The section an article belongs to: the blog, or the event photography pages. */
export interface ArticleSection {
  name: string;
  path: string;
}
export const BLOG_SECTION: ArticleSection = { name: "Blog", path: BLOG_PATH };

export const Crumbs = ({ title, section = BLOG_SECTION }: { title?: string; section?: ArticleSection }) => (
  <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1 text-sm text-white/70">
      <li>
        <Link to="/" className="hover:text-white">
          Home
        </Link>
      </li>
      <li aria-hidden="true">
        <ChevronRight size={14} />
      </li>
      <li>{title ? <Link to={section.path} className="hover:text-white">{section.name}</Link> : <span aria-current="page">{section.name}</span>}</li>
    </ol>
  </nav>
);

/** An article's hero image, under the header: eager-loaded (it's the largest thing in view), with its caption. */
const ArticleHero = ({ hero }: { hero: NonNullable<BlogPost["hero"]> }) => {
  const img = GUIDE_IMAGES[hero.image];
  if (!img) return null;
  return (
    <figure className="guide-figure mb-10 mt-0" data-testid="article-hero">
      <div className="overflow-hidden rounded-xl border border-border shadow-sm">
        <GuideImg img={img} alt={hero.alt} eager sizes="(min-width: 768px) 720px, 100vw" />
      </div>
      {hero.caption && <figcaption>{renderInline(hero.caption, guideLink, "hero")}</figcaption>}
    </figure>
  );
};

export const BlogArticle = ({ post, guidesOn, newHome, section = BLOG_SECTION }: { post: BlogPost; guidesOn: boolean; newHome: boolean; section?: ArticleSection }) => {
  const related = guidesOn ? post.relatedGuides.map((s) => getGuide(s)).filter((g) => !!g) : [];
  const relatedPosts = post.relatedPosts.map((s) => getPost(s)).filter((p): p is BlogPost => !!p);
  const href = (h: string) => blogHref(h, newHome);
  const blocks = mapBlockLinks(post.blocks, href);
  const byline = findAuthor(post.author)?.name ?? "Photohawk";
  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />
      <main>
        <article data-testid="blog-post">
          <header className="relative overflow-hidden bg-hawk-navy pb-14 pt-28 text-white md:pb-16 md:pt-32">
            <div className="absolute inset-0 bg-radial-orange opacity-20" aria-hidden="true" />
            <div className="container relative z-10 mx-auto max-w-3xl px-4">
              <Crumbs title={post.title} section={section} />
              {post.product && (
                <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-hawk-gold" data-testid="blog-product">
                  {BLOG_PRODUCTS[post.product]}
                </p>
              )}
              <h1 className="mb-4 font-display text-[clamp(1.875rem,4.5vw,2.75rem)] font-bold leading-tight">{post.title}</h1>
              <p className="text-sm text-white/75">
                By {byline} · <time dateTime={post.published}>{formatGuideDate(post.published)}</time>
                {post.updated && (
                  <>
                    {" "}
                    · Updated <time dateTime={post.updated}>{formatGuideDate(post.updated)}</time>
                  </>
                )}{" "}
                · {readingMinutes(post.words)} min read
              </p>
            </div>
          </header>
          <div className="container mx-auto max-w-3xl px-4 py-12">
            {post.hero && <ArticleHero hero={post.hero} />}
            {post.editorNote && (
              <aside className="guide-callout guide-callout--note mb-8 max-w-[68ch] [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2" aria-label="Editor's note" data-testid="editor-note">
                <p className="guide-callout__title">
                  <Info size={18} aria-hidden="true" />
                  <span>Editor's note, {formatGuideDate(post.updated ?? post.published)}</span>
                </p>
                <p>{renderInline(post.editorNote, guideLink, "note")}</p>
              </aside>
            )}
            <div className="guide-prose">
              <GuideBlocks blocks={blocks} />
              {post.faq.length > 0 && (
                <section aria-labelledby="faq" data-testid="blog-faq">
                  <h2 id="faq">Questions people ask</h2>
                  {post.faq.map((f, i) => (
                    <div key={i} className="guide-faq">
                      <h3>{f.q}</h3>
                      <p>{renderInline(mapInlineLinks(f.a, href), guideLink, `faq${i}`)}</p>
                    </div>
                  ))}
                </section>
              )}
            </div>
            {!isNewArticle(post) && (
              <p className="mt-10 max-w-[68ch] border-t border-border pt-4 text-sm text-muted-foreground">
                First published on the Photohawk blog on {formatGuideDate(post.published)}.
              </p>
            )}
            {relatedPosts.length > 0 && (
              <section aria-labelledby="blog-more" className="mt-10 max-w-[68ch] rounded-2xl border border-border bg-card p-6" data-testid="blog-more">
                <h2 id="blog-more" className="mb-3 font-display text-xl font-bold">
                  More from the blog
                </h2>
                <ul className="m-0 list-none space-y-2 p-0">
                  {relatedPosts.map((r) => (
                    <li key={r.slug}>
                      <Link to={blogPath(r.slug)} className="inline-flex items-center gap-1.5 font-semibold text-[hsl(18_100%_38%)] hover:underline">
                        {r.title}
                        <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {related.length > 0 && (
              <section aria-labelledby="blog-related" className="mt-8 max-w-[68ch] rounded-2xl border border-border bg-card p-6" data-testid="blog-related">
                <h2 id="blog-related" className="mb-3 font-display text-xl font-bold">
                  Read more in our guides
                </h2>
                <ul className="m-0 list-none space-y-2 p-0">
                  {related.map((g) => (
                    <li key={g!.slug}>
                      <Link to={guidePath(g!.slug)} className="inline-flex items-center gap-1.5 font-semibold text-[hsl(18_100%_38%)] hover:underline">
                        {g!.title}
                        <ArrowRight size={15} aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

/** /blog/:slug */
export const BlogPostPage = () => {
  const { slug = "" } = useParams();
  const host = useHostname();
  const guidesOn = isGuidesEnabled(host);
  const post = getPost(slug);
  if (!post) return <NotFound />;
  if (guidesOn && post.supersededBy) return <SsrNavigate to={guidePath(post.supersededBy)} replace permanent />;
  return <BlogArticle post={post} guidesOn={guidesOn} newHome={isNewHomeEnabled(host)} />;
};

/** /blog */
export const BlogIndexPage = () => {
  const guidesOn = isGuidesEnabled(useHostname());
  const sora = useSoraHeadings();
  const posts = guidesOn ? BLOG_POSTS.filter((p) => !p.supersededBy) : BLOG_POSTS;
  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />
      <main>
        <section className="relative overflow-hidden bg-hawk-navy pb-14 pt-32 text-white">
          <div className="absolute inset-0 bg-radial-orange opacity-20" aria-hidden="true" />
          <div className="container relative z-10 mx-auto max-w-4xl px-4">
            <Crumbs />
            <h1 className={`mb-3 font-display text-4xl font-bold md:text-5xl${soraOnly(sora, "max-md:text-[2rem]")}`}>The Photohawk blog</h1>
            <p className="max-w-2xl text-lg text-white/80">{BLOG_INDEX_SEO.description}</p>
            {guidesOn && (
              <Link to={GUIDES_PATH} className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-hawk-orange to-hawk-gold px-5 font-semibold text-white">
                Read our new in-depth guides
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            )}
          </div>
        </section>
        <section className="py-12">
          <div className="container mx-auto max-w-4xl px-4">
            <ul className="m-0 grid list-none gap-4 p-0" data-testid="blog-list">
              {posts.map((p) => (
                <li key={p.slug}>
                  <Link to={postHref(p, guidesOn)} className="group flex gap-5 rounded-2xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
                    <span className="min-w-0 flex-1">
                      {p.product && <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{BLOG_PRODUCTS[p.product]}</span>}
                      <h2 className="mb-1 font-display text-xl font-bold leading-snug group-hover:text-hawk-orange">{p.title}</h2>
                      <span className="mb-2 block text-sm text-muted-foreground">
                        <time dateTime={p.published}>{formatGuideDate(p.published)}</time> · {readingMinutes(p.words)} min read
                      </span>
                      <span className="block text-foreground/80">{p.description}</span>
                    </span>
                    {p.hero && GUIDE_IMAGES[p.hero.image] && (
                      <span className="hidden w-48 flex-none overflow-hidden rounded-lg border border-border sm:block">
                        <GuideImg img={GUIDE_IMAGES[p.hero.image]} alt="" sizes="192px" className="block aspect-[16/10] h-auto w-full object-cover" />
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
